import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { Sentry } from "@/lib/sentry";

interface Profile {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
  role: "admin" | "editor" | "reader";
}

interface AuthContextValue {
  isConfigured: boolean;
  isLoading: boolean;
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (payload: { name: string; avatar_url: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchProfile(userId: string): Promise<Profile | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from("app_users").select("*").eq("id", userId).maybeSingle();
  if (error || !data) return null;
  return data as Profile;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);

  const refreshProfile = async () => {
    if (!user?.id) {
      setProfile(null);
      return;
    }
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setIsLoading(false);
      return;
    }

    const loadingSafetyTimer = window.setTimeout(() => {
      setIsLoading(false);
    }, 4000);

    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        setSession(data.session);
        setUser(data.session?.user ?? null);
        if (data.session?.user?.id) {
          const p = await fetchProfile(data.session.user.id);
          setProfile(p);
        }
      })
      .catch(() => {
        setSession(null);
        setUser(null);
        setProfile(null);
      })
      .finally(() => {
        setIsLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsLoading(false);
      if (newSession?.user?.id) {
        Sentry.setUser({ id: newSession.user.id, email: newSession.user.email });
        fetchProfile(newSession.user.id)
          .then((p) => setProfile(p))
          .catch(() => setProfile(null));
      } else {
        Sentry.setUser(null);
        setProfile(null);
      }
    });

    return () => {
      window.clearTimeout(loadingSafetyTimer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isConfigured: isSupabaseConfigured,
      isLoading,
      user,
      session,
      profile,
      async signIn(email: string, password: string) {
        if (!supabase) throw new Error("Supabase no está configurado.");
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      async signOut() {
        setSession(null);
        setUser(null);
        setProfile(null);
        if (!supabase) return;
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      },
      async sendPasswordReset(email: string) {
        if (!supabase) throw new Error("Supabase no está configurado.");
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });
        if (error) throw error;
      },
      async updatePassword(password: string) {
        if (!supabase) throw new Error("Supabase no está configurado.");
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
      },
      async refreshProfile() {
        await refreshProfile();
      },
      async updateProfile(payload: { name: string; avatar_url: string }) {
        if (!supabase || !user?.id) throw new Error("No hay sesión activa.");
        const { error } = await supabase
          .from("app_users")
          .update({ name: payload.name, avatar_url: payload.avatar_url })
          .eq("id", user.id);
        if (error) throw error;
        await refreshProfile();
      },
    }),
    [isLoading, user, session, profile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
